import React, { useRef, useState, useEffect, useCallback } from 'react';
import { testimonialsData } from '../data/mockData';
import { Link } from 'react-router-dom';

const AUTOPLAY_DURATION = 8000; // 8 seconds per slide

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const progressRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % testimonialsData.length);
    setProgress(0);
    startTimeRef.current = Date.now();
  }, []);

  // Animate progress bar
  useEffect(() => {
    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min(elapsed / AUTOPLAY_DURATION, 1);
      setProgress(pct);
      if (pct >= 1) {
        goToNext();
      } else {
        progressRef.current = requestAnimationFrame(animate);
      }
    };
    startTimeRef.current = Date.now();
    progressRef.current = requestAnimationFrame(animate);
    return () => {
      if (progressRef.current) cancelAnimationFrame(progressRef.current);
    };
  }, [activeIndex, goToNext]);

  const handleDotClick = (i) => {
    setActiveIndex(i);
    setProgress(0);
    startTimeRef.current = Date.now();
  };

  const testimonial = testimonialsData[activeIndex];

  // Map testimonials to customer story slugs
  const storyLinks = [
    '/customer-stories/how-allen-recruitment-leverages-their-ai-recruiter',
    '/customer-stories/university-of-alberta-case-study',
    '/customer-stories/cfos-domain'
  ];

  return (
    <section ref={ref} className="bg-[#F9FBF5] py-20 lg:py-32">
      <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
        <div
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Link
            to={storyLinks[activeIndex] || '#'}
            className="block group"
          >
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
              {/* Avatar */}
              <div className="w-48 h-48 lg:w-64 lg:h-72 rounded-xl overflow-hidden flex-shrink-0">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Quote */}
              <div className="flex-1">
                <blockquote className="text-[#082900] text-[20px] lg:text-[32px] font-light leading-[1.2] tracking-[-0.011em] mb-8 italic">
                  {testimonial.quote}
                </blockquote>

                {/* Author */}
                <p className="text-[#082900] text-[14px] lg:text-[16px] font-normal leading-[24px]">
                  {testimonial.name}
                </p>
                <p className="text-[#082900]/60 text-[14px] lg:text-[16px] font-normal leading-[24px]">
                  {testimonial.title}
                </p>

                {/* Signature/Company Logo */}
                <div className="mt-6">
                  <img
                    src={testimonial.companyLogo}
                    alt="Signature"
                    className="h-10 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </Link>

          {/* Progress Bar Pagination (dutyai.com style) */}
          <div className="flex items-center gap-2 mt-12 lg:mt-16">
            {testimonialsData.map((_, i) => (
              <button
                key={i}
                onClick={() => handleDotClick(i)}
                className="flex-1 h-[3px] bg-[#082900]/10 rounded-full overflow-hidden cursor-pointer relative"
                aria-label={`Go to testimonial ${i + 1}`}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-[#082900] rounded-full transition-none"
                  style={{
                    width: i === activeIndex
                      ? `${progress * 100}%`
                      : i < activeIndex
                        ? '100%'
                        : '0%'
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
